import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { COLORS } from "../lib/colors";
import { PoweredBy } from "../components/PoweredBy";
import { track, Events } from "../lib/analytics";
import {
  bumpVisits,
  fontForVisit,
  loadFontFamily,
  TOTAL_FONTS,
  BAG_OUTLINE_VISIT,
} from "../lib/wordmark";
import { BagOutline } from "../components/BagOutline";

const INSTAGRAM_URL = "https://www.instagram.com/kisubikashop";

/**
 * KISUBIKA — Shop landing.
 *
 * Yeezy.com direction: pure white, no decoration, no marketing copy.
 * Just the wordmark and a flat 20-product grid (one per blue), with
 * brutalist product codes underneath. Click a product → AR preview.
 */
const Landing = () => {
  const [visit, setVisit] = useState(null);
  const [font, setFont] = useState(null);

  useEffect(() => {
    // Each mount counts as one visit. Increment, pick the matching font,
    // load it from Google Fonts, then apply.
    const v = bumpVisits();
    const f = fontForVisit(v);
    setVisit(v);
    setFont(f);
    if (f) loadFontFamily(f);
    window.scrollTo(0, 0);
  }, []);

  const unlocked = visit !== null && visit >= BAG_OUTLINE_VISIT;
  const visitLabel = visit
    ? unlocked
      ? `VISIT ${visit} · UNLOCKED`
      : `VISIT ${String(visit).padStart(3, "0")} / ${BAG_OUTLINE_VISIT}`
    : "";

  return (
    <div className="min-h-screen bg-white text-black flex flex-col">
      {/* Top utility bar — minimal, fixed-height, no big nav */}
      <header
        data-testid="shop-header"
        className="border-b border-black px-4 sm:px-6 py-3 flex items-center justify-between text-[11px] tracking-[0.18em] uppercase"
      >
        <Link to="/" className="hover:underline">
          ← Cover
        </Link>
        <div className="hidden sm:flex items-center gap-8">
          <Link to="/shop" className="font-semibold">
            Shop
          </Link>
          <Link to="/ar" className="hover:underline">
            AR Preview
          </Link>
          <Link to="/reservations" className="hover:underline">
            Reservations
          </Link>
        </div>
        <a
          href={INSTAGRAM_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="hover:underline"
        >
          IG
        </a>
      </header>

      {/* Wordmark — typeface cycles every visit; outline replaces text after the 100th */}
      <section className="px-4 sm:px-6 pt-10 sm:pt-14 pb-6 sm:pb-10">
        {unlocked ? (
          <BagOutline
            data-testid="wordmark-outline"
            className="w-full h-auto max-h-[42vh] text-black"
            strokeWidth={5}
          />
        ) : (
          <h1
            data-testid="hero-headline"
            className="uppercase leading-none tracking-[0.04em] text-[22vw] sm:text-[19vw] text-black relative inline-block"
            style={{
              fontFamily: font
                ? `"${font}", "Hanken Grotesk", "Helvetica Neue", sans-serif`
                : '"Hanken Grotesk", "Helvetica Neue", sans-serif',
              fontWeight: 600,
              letterSpacing: "-0.03em",
            }}
          >
            KISUBIKA
            <sup className="absolute top-[8%] -right-3 sm:-right-5 text-[1.1vw] tracking-[0.1em] font-semibold kb-blue">
              AR
            </sup>
          </h1>
        )}
        <div className="mt-2 sm:mt-3 flex flex-wrap items-end justify-between gap-2 text-[11px] tracking-[0.22em] uppercase">
          <div>SS·26 RELEASE — 20 BLUES · 1 TOTE · £20 DEPOSIT</div>
          <div className="flex items-center gap-3">
            <span className="opacity-60">100 FACES. 100 AGES. ONE LIFE.</span>
            <span
              data-testid="visit-counter"
              className="kb-blue font-semibold tabular-nums"
            >
              {visitLabel}
            </span>
          </div>
        </div>
        {!unlocked && font && (
          <div className="mt-2 text-[10px] tracking-[0.22em] uppercase opacity-50">
            Today’s wordmark: {font}
          </div>
        )}
      </section>

      {/* Product grid — Yeezy style: flat, dense, no decoration */}
      <section className="px-4 sm:px-6 pb-12">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-x-4 sm:gap-x-6 gap-y-10 sm:gap-y-14">
          {COLORS.map((c, i) => {
            const code = `KSB-${String(i + 1).padStart(3, "0")}`;
            return (
              <Link
                key={c.id}
                to="/ar"
                state={{ colorId: c.id }}
                data-testid={`shop-product-${c.id}`}
                onClick={() =>
                  track(Events.ColorSelected, {
                    source: "shop_grid",
                    color_id: c.id,
                    color_name: c.name,
                  })
                }
                className="group block"
              >
                <div className="relative aspect-square w-full bg-[#FAFAFA] overflow-hidden">
                  <div
                    className="kb-bag absolute inset-3 sm:inset-5 transition-transform duration-500 ease-out group-hover:scale-[1.04]"
                    style={{ "--kb-tint": c.filter }}
                  />
                </div>
                <div className="mt-3 flex items-baseline justify-between text-[11px] tracking-[0.18em] uppercase">
                  <span className="font-semibold">{code}</span>
                  <span className="opacity-60 truncate ml-2">{c.name}</span>
                </div>
                <div className="mt-1 text-[10px] tracking-[0.2em] uppercase opacity-60">
                  £20 · DEPOSIT
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Catalog row — text-only product index, Yeezy footer style */}
      <section className="border-t border-black px-4 sm:px-6 py-6 text-[11px] tracking-[0.18em] uppercase">
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-y-2">
          {COLORS.map((c, i) => (
            <Link
              key={`idx-${c.id}`}
              to="/ar"
              state={{ colorId: c.id }}
              className="hover:underline truncate"
            >
              {String(i + 1).padStart(3, "0")} / {c.name}
            </Link>
          ))}
        </div>
      </section>

      {/* Footer — Yeezy minimalism */}
      <footer
        data-testid="shop-footer"
        className="mt-auto border-t border-black px-4 sm:px-6 py-5 flex flex-wrap items-center justify-between gap-3 text-[11px] tracking-[0.18em] uppercase"
      >
        <div className="flex flex-wrap items-center gap-5">
          <Link to="/reservations" className="hover:underline">
            Reservations
          </Link>
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline"
          >
            Instagram
          </a>
          <Link to="/" className="hover:underline">
            Cover
          </Link>
          <a href="mailto:hello@kisubika.shop" className="hover:underline">
            Contact
          </a>
        </div>
        <div className="flex items-center gap-5">
          <span className="opacity-60">© 2026 KISUBIKA</span>
          <PoweredBy />
        </div>
      </footer>
    </div>
  );
};

export default Landing;
